import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ModeTabsSlot } from "@/app/components/ModeTabs/ModeTabsPortal";
import { BackgroundStripes } from "@/ui/BackgroundStripes/BackgroundStripes";
import { useWithdrawModalStore } from "@/apps/goals/store/withdrawModalStore";
import { useStartupParamsStore } from "@/store/startupParams";
import { requestRafflePopupSeen } from "@/apps/raffle/services/raffleApi";
import { formatRafflePeriod } from "@/apps/raffle/pages/RafflePage/utils";
import { ParticipationConditions } from "./ParticipationConditions";
import { RafflePageLoader } from "./RafflePageLoader";
import { PrizeSummaryCard } from "./PrizeSummaryCard";
import { RaffleLeaderboard } from "./RaffleLeaderboard";
import { RaffleHistoryButton } from "./RaffleHistoryButton";
import { RaffleTimer } from "./RaffleTimer";
import { RaffleWheelSection } from "./RaffleWheelSection";
import { RafflePageProvider, useRafflePageData } from "./RafflePageContext";
import { RaffleEmptyState } from "./RaffleEmptyState";
import { WonPrizeModal } from "./WonPrizeModal";
import { RaffleHistoryBackIcon } from "./icons";
import cls from "./styles.module.scss";

export const RafflePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const yandexId = useStartupParamsStore((state) => state.auth.yandexId);
  const openWithdrawModal = useWithdrawModalStore((state) => state.open);
  const [isLoaderExiting, setIsLoaderExiting] = useState(false);
  const [isClaimingPrize, setIsClaimingPrize] = useState(false);
  const [dismissedWonPopupId, setDismissedWonPopupId] = useState<number | null>(
    null
  );

  const { bonusId } = useParams();
  const isRaffleDetailPage = Boolean(bonusId);

  const { isLoading, isEmpty, raffleData } = useRafflePageData();
  const wasLoadingRef = useRef(isLoading);
  const isFinishedRaffle = Boolean(raffleData?.raffle.drawn_at);
  const wonPrizeAmount = raffleData?.raffle.me.prize_amount;
  const shouldShowResultsAnimation = Boolean(
    !isRaffleDetailPage && raffleData?.results?.winners.length
  );

  const shouldShowWonPrizeModal = Boolean(
    isRaffleDetailPage &&
      raffleData?.raffle.me.show_win_popup &&
      wonPrizeAmount !== null &&
      wonPrizeAmount !== undefined &&
      dismissedWonPopupId !== raffleData.raffle.id
  );
  const finishedRaffleTitle =
    raffleData &&
    `${t("app.modes.raffle")} ${formatRafflePeriod(
      raffleData.raffle.period_start,
      raffleData.raffle.period_end
    )}`;
  const hasGrayBackground = isRaffleDetailPage;
  const rootClassName = [cls.root, hasGrayBackground ? cls.rootGray : ""].join(
    " "
  );
  const isLoaderVisible = isLoading || isLoaderExiting;

  useEffect(() => {
    if (isLoading) {
      wasLoadingRef.current = true;
      setIsLoaderExiting(false);
      return;
    }

    if (!wasLoadingRef.current) return;

    wasLoadingRef.current = false;
    setIsLoaderExiting(true);

    const timeoutId = window.setTimeout(() => {
      setIsLoaderExiting(false);
    }, 360);

    return () => window.clearTimeout(timeoutId);
  }, [isLoading]);

  const dismissWonPopup = async (openBalanceAfterDismiss = false) => {
    if (!yandexId || !raffleData || isClaimingPrize) return;

    setIsClaimingPrize(true);

    try {
      await requestRafflePopupSeen(yandexId, raffleData.raffle.id);
      setDismissedWonPopupId(raffleData.raffle.id);
      if (openBalanceAfterDismiss) {
        openWithdrawModal();
      }
    } catch (error) {
      console.error("Failed to dismiss raffle win popup", error);
    } finally {
      setIsClaimingPrize(false);
    }
  };

  if (isLoaderVisible)
    return (
      <section className={rootClassName}>
        <BackgroundStripes color="gray" className={cls.backgroundStripes} />
        <div className={cls.content}>
          <ModeTabsSlot className={cls.modeTabs} />
          <RafflePageLoader isExiting={!isLoading} />
        </div>
      </section>
    );

  if (isEmpty) return <RaffleEmptyState rootClassName={rootClassName} />;

  if (!raffleData) return null;

  return (
    <RafflePageProvider raffleData={raffleData}>
      <section className={rootClassName}>
        <BackgroundStripes
          color={hasGrayBackground ? "gray" : "yellow"}
          className={cls.backgroundStripes}
        />
        <div className={[cls.content, cls.contentReady].join(" ")}>
          <ModeTabsSlot className={cls.modeTabs} />
          {isFinishedRaffle && (
            <button
              type="button"
              className={cls.detailBackButton}
              onClick={() => navigate("/apps/raffle/history")}
            >
              <RaffleHistoryBackIcon />
              <span>{finishedRaffleTitle}</span>
            </button>
          )}
          <PrizeSummaryCard />
          <RaffleWheelSection />
          <div className={cls.belowWheel}>
            {!shouldShowResultsAnimation && <RaffleTimer />}
            <ParticipationConditions />
            <RaffleLeaderboard />
            {!isFinishedRaffle && <RaffleHistoryButton />}
          </div>
        </div>
        {isRaffleDetailPage && !!wonPrizeAmount && (
          <WonPrizeModal
            prizeAmount={wonPrizeAmount}
            rides={raffleData.raffle.me.rides}
            periodIncome={raffleData.raffle.me.period_income}
            isOpen={shouldShowWonPrizeModal}
            isClaiming={isClaimingPrize}
            onClose={() => dismissWonPopup()}
            onClaimPrize={() => dismissWonPopup(true)}
          />
        )}
      </section>
    </RafflePageProvider>
  );
};
